import type { ThemeVariantStyles } from 'Src/types/theme';
import React, { Children } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { noop, isEmpty } from 'lodash';
import type { TrackingPayload } from 'Tracking/tracking-api/useTracking';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { themes } from './NavTag.themes';

type ThemesResult = ReturnType<typeof themes>;

export type Props = {
  children?: React.ReactNode;
  className?: string;
  colorActive?: Colors;
  colorUnactive?: Colors;
  href?: string;
  isActive?: boolean;
  onClick?: ((arg0: React.SyntheticEvent) => void) | null;
  theme?: keyof ThemesResult;
  trackingData?: TrackingPayload | null;
};
export const NavTag = ({
  children,
  className,
  colorActive,
  colorUnactive,
  href,
  isActive,
  onClick,
  theme = 'default',
  trackingData = {},
  ...rest
}: Props) => {
  const tracker = useTracking();
  const track =
    !isEmpty(trackingData) && onClick && trackingData ? () => tracker(trackingData) : noop;

  if (!Children.count(children)) {
    return null;
  }

  let TagName: keyof JSX.IntrinsicElements = 'div';

  if (href) {
    TagName = 'a';
  } else if (onClick) {
    TagName = 'button';
  }

  const { thRoot } = themes({
    colorActive,
    colorUnactive,
    isActive,
  })[theme] as ThemeVariantStyles<typeof themes>;
  return (
    <TagName
      data-testid="Tag"
      {...rest}
      className={classnames(thRoot, className)}
      onClick={(e) => {
        onClick?.(e);
        track();
      }}
      href={href}
      {...(onClick && TagName === 'button' ? { type: 'button' as const } : null)}
    >
      {children}
    </TagName>
  );
};
